import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { Material, MaterialAttachment } from '../models/material.model';

@Injectable({
  providedIn: 'root',
})
export class MaterialsService {
  private http = inject(HttpClient);
  private apiUrl = '/quotes-api/materials/';
  private attachmentApiUrl = '/quotes-api/material-attachments/';

  private materialsSubject = new BehaviorSubject<Material[]>([]);
  public materials$ = this.materialsSubject.asObservable();

  private activeMaterialsSubject = new BehaviorSubject<Material[]>([]);
  public activeMaterials$ = this.activeMaterialsSubject.asObservable();

  constructor() {
    this.loadMaterials();
  }

  /**
   * Load materials from the API and update the materials$ observable
   */
  async loadMaterials(): Promise<void> {
    const materials = await firstValueFrom(
      this.http.get<Material[]>(this.apiUrl),
    );
    this.activeMaterialsSubject.next(
      materials.filter((material) => !material.is_archived),
    );
    this.materialsSubject.next(materials);
  }

  /**
   * Get a specific material by ID
   * @param id Material ID
   * @returns Observable with material
   */
  getMaterial$(id: number): Observable<Material> {
    return this.http.get<Material>(`${this.apiUrl}${id}/`);
  }

  /**
   * Create a new material and refresh the materials list
   * @param material Material data to create
   * @returns Promise with created material
   */
  async createMaterial(material: Partial<Material>): Promise<Material> {
    const created = await firstValueFrom(
      this.http.post<Material>(this.apiUrl, material),
    );
    await this.loadMaterials();
    return created;
  }

  /**
   * Update a material (using PUT) and refresh the materials list
   * @param id Material ID
   * @param material Material data
   * @returns Promise with updated material
   */
  async putMaterial(
    id: number,
    material: Partial<Material>,
  ): Promise<Material> {
    const updated = await firstValueFrom(
      this.http.put<Material>(`${this.apiUrl}${id}/`, material),
    );
    await this.loadMaterials();
    return updated;
  }

  /**
   * Archive a material and refresh the materials list
   * @param id Material ID
   * @returns Promise with archived material
   */
  async archiveMaterial(id: number): Promise<Material> {
    const archived = await firstValueFrom(
      this.http.patch<Material>(`${this.apiUrl}${id}/`, { is_archived: true }),
    );
    await this.loadMaterials();
    return archived;
  }

  async unarchiveMaterial(id: number): Promise<Material> {
    const archived = await firstValueFrom(
      this.http.patch<Material>(`${this.apiUrl}${id}/`, {
        is_archived: false,
      }),
    );
    await this.loadMaterials();
    return archived;
  }

  /**
   * Upload an attachment file to a material
   * @param materialId Material ID
   * @param file File to upload
   * @returns Promise with created attachment
   */
  async uploadAttachment(
    materialId: number,
    file: File,
    refresh: boolean = true,
  ): Promise<MaterialAttachment> {
    const formData = new FormData();
    formData.append('material_id', materialId.toString());
    formData.append('file', file);

    const attachment = await firstValueFrom(
      this.http.post<MaterialAttachment>(this.attachmentApiUrl, formData),
    );
    if (refresh) {
      await this.loadMaterials();
    }
    return attachment;
  }

  /**
   * Delete an attachment
   * @param attachmentId Attachment ID
   */
  async deleteAttachment(
    attachmentId: number,
    refresh: boolean = true,
  ): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.attachmentApiUrl}${attachmentId}/`),
    );
    if (refresh) {
      await this.loadMaterials();
    }
  }
}
